// src/scene/SceneCanvas.js
//
// React wrapper around the shared Three.js scene. Each mount creates its own
// <canvas> (a disposed renderer loses its context for good, so a canvas is
// never reused — this also keeps StrictMode's double-mount safe).

import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { mount } from './tsuinoScene';

const SceneCanvas = forwardRef(function SceneCanvas(
    { mode = 'hero', flesh = false, className, style },
    ref
) {
    const hostRef = useRef(null);
    const sceneRef = useRef(null);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;
        const canvas = document.createElement('canvas');
        canvas.setAttribute('aria-hidden', 'true');
        Object.assign(canvas.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block' });
        host.appendChild(canvas);
        let scene = null;
        try {
            scene = mount(canvas, { mode, flesh, pauseWhenIdle: false });
        } catch (e) {
            // WebGL unavailable: the flat CG / gradient behind the canvas still carries the page
            console.warn('WebGL scene disabled:', e);
        }
        sceneRef.current = scene;
        return () => {
            if (scene) scene.dispose();
            sceneRef.current = null;
            canvas.remove();
        };
    }, [mode, flesh]);

    useImperativeHandle(ref, () => ({
        setHighlight: (i) => sceneRef.current && sceneRef.current.setHighlight(i),
        setOpen: (v) => sceneRef.current && sceneRef.current.setOpen(v),
    }), []);

    return (
        <div
            ref={hostRef}
            className={className}
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...style }}
        />
    );
});

export default SceneCanvas;
